import { getDomainConfig, rewriteUrlOrigin } from '@/config/domainConfig';
import type { Artifact } from '@/types/artifact';

/**
 * The URL an artifact frame loads.
 *
 * With the render flag on and the server answering with `html_url`, that path
 * is pinned to this brand's own API origin, the way `insight.html_url` is: it
 * is relative, and on a white-label domain the page origin is not the API's.
 * The browser then follows the server's redirect to the artifact domain, and
 * every relative reference inside the page resolves under the view token.
 *
 * Otherwise the presigned `url`, used exactly as given: it is signed as a whole.
 *
 * @param artifact - The artifact as read, or undefined before the read lands.
 * @param useRender - Whether the render flag is on.
 *
 * @returns The frame `src`, or undefined with nothing to show yet.
 */
export function artifactFrameSrc(
  artifact: Pick<Artifact, 'url' | 'html_url'> | undefined,
  useRender: boolean,
): string | undefined {
  if (!artifact) return undefined;
  if (useRender && artifact.html_url) {
    return renderRouteSrc(artifact.html_url);
  }
  return artifact.url;
}

/**
 * A render route as a frame loads it: the server hands it out relative, and
 * on a white-label domain the page origin is not the API's, so it is pinned
 * to this brand's API origin. One rule for every frame that loads one, signed
 * in or opened from a public link.
 *
 * @param htmlUrl - The relative render route, `/render/{token}/artifact/{id}/`.
 *
 * @returns The frame `src`.
 */
export function renderRouteSrc(htmlUrl: string): string {
  return rewriteUrlOrigin(htmlUrl, getDomainConfig().apiBaseUrl);
}
